import { z } from "zod";

// --- Plans ---------------------------------------------------------------------------------------

export const planIdSchema = z.enum(["personal", "team"]);
export type PlanId = z.infer<typeof planIdSchema>;
export const billingIntervalSchema = z.enum(["month", "year"]);
export type BillingInterval = z.infer<typeof billingIntervalSchema>;

const GIB = 1024 ** 3;

export interface Plan {
  id: PlanId;
  /** Per mailbox, in cents; null when the plan is sold yearly only. */
  monthlyMinor: number | null;
  /** Per mailbox per year, in cents: two months free. */
  yearlyMinor: number;
  mailboxQuotaBytes: number;
  features: { notes: boolean };
}

/**
 * What we sell at launch (ADR 0002). Prices are per mailbox in US dollars; the payment provider
 * holds the same amounts as configured prices.
 */
export const PLANS: Record<PlanId, Plan> = {
  personal: {
    id: "personal",
    // Yearly only: the card fee's fixed 30 cents would eat a seventh of a $2.90 monthly charge.
    monthlyMinor: null,
    yearlyMinor: 2900,
    mailboxQuotaBytes: 10 * GIB,
    features: { notes: false },
  },
  team: {
    id: "team",
    monthlyMinor: 690,
    yearlyMinor: 6900,
    mailboxQuotaBytes: 25 * GIB,
    features: { notes: true },
  },
};

/** How a plan is billed when the customer prefers `wanted`: yearly-only plans stay yearly. */
export function intervalFor(plan: PlanId, wanted: BillingInterval): BillingInterval {
  return PLANS[plan].monthlyMinor === null ? "year" : wanted;
}

/** Price per mailbox for one billing period, in cents. */
export function unitPrice(plan: PlanId, interval: BillingInterval): number {
  const { monthlyMinor, yearlyMinor } = PLANS[plan];
  return interval === "month" && monthlyMinor !== null ? monthlyMinor : yearlyMinor;
}

/** New workspaces try the fullest plan we sell. */
export const TRIAL_PLAN: PlanId = "team";

// --- State --------------------------------------------------------------------------------------

/**
 * What the workspace may do right now:
 * - trial: free trial, no card yet (sending outside the workspace needs a card after a few days)
 * - active: paid, or trialing with a card on file
 * - past_due: a payment failed; everything works while the provider retries
 * - expired: the trial ended or the subscription was cancelled; mail still arrives and can be read,
 *   but nothing new can be sent or created until a plan is chosen
 */
export const billingAccessSchema = z.enum(["trial", "active", "past_due", "expired"]);
export type BillingAccess = z.infer<typeof billingAccessSchema>;

/** What every member's client needs to show or hide features and banners. */
export const entitlementsSchema = z.object({
  plan: planIdSchema,
  access: billingAccessSchema,
  trialEndsAt: z.string().nullable(),
  features: z.object({ notes: z.boolean() }),
  /**
   * False during a card-less trial after the grace days, when access expired, or while sending
   * is paused.
   */
  canSendExternal: z.boolean(),
  /** Outside sending is paused for bounces or complaints until support clears it. */
  sendingPaused: z.boolean(),
});
export type Entitlements = z.infer<typeof entitlementsSchema>;

/** The billing page of the admin console. */
export const billingSummarySchema = z.object({
  plan: planIdSchema,
  interval: billingIntervalSchema,
  access: billingAccessSchema,
  trialEndsAt: z.string().nullable(),
  /** Next renewal of a paid subscription. */
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  /** Paid plan chosen during the trial: nothing is charged before currentPeriodEnd. */
  firstChargePending: z.boolean(),
  /** A subscription exists at the payment provider, so a card is on file. */
  hasPaymentMethod: z.boolean(),
  /** Mailboxes billed; at least one. */
  seats: z.number().int().positive(),
  /** Seats times the plan's price for the interval, in cents; 0 when complimentary. */
  amountMinor: z.number().int().nonnegative(),
  currency: z.literal("USD"),
  /** The platform's own workspace: full access, never billed. */
  complimentary: z.boolean(),
});
export type BillingSummary = z.infer<typeof billingSummarySchema>;

export const billingPlanRequestSchema = z.object({
  plan: planIdSchema,
  interval: billingIntervalSchema,
});
export type BillingPlanRequest = z.infer<typeof billingPlanRequestSchema>;

/** Either the provider's hosted page to go to, or the change was applied at once. */
export const billingRedirectSchema = z.object({ url: z.string().nullable() });
export type BillingRedirect = z.infer<typeof billingRedirectSchema>;
